import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import AppLayout from "@/components/AppLayout";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import { Vendors, Customers, Technicians, Items } from "@/pages/Masters";
import TransactionForm from "@/pages/TransactionForm";
import { TechIssue, TechReturn } from "@/pages/TechFlow";
import { StockReport, LowStock, StockMovements } from "@/pages/Reports";
import InvoicePrint from "@/pages/InvoicePrint";
import TechnicianLedger from "@/pages/TechnicianLedger";
import Settings from "@/pages/Settings";
import Quotations from "@/pages/Quotations";
import Profile from "@/pages/Profile";

function Private({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <AppLayout>{children}</AppLayout>;
}

function AppRouter() {
  const location = useLocation();
  // CRITICAL: synchronously detect session_id in URL fragment BEFORE any protected route runs.
  // useLocation().hash is reactive; window.location.hash is not.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/public/invoice/:id" element={<InvoicePrint />} />
      <Route path="/public/quote/:id" element={<InvoicePrint />} />
      <Route path="/" element={<Private><Dashboard /></Private>} />
      <Route path="/dashboard" element={<Private><Dashboard /></Private>} />
      <Route path="/customers" element={<Private><Customers /></Private>} />
      <Route path="/vendors" element={<Private><Vendors /></Private>} />
      <Route path="/technicians" element={<Private><Technicians /></Private>} />
      <Route path="/items" element={<Private><Items /></Private>} />
      <Route path="/purchases" element={<Private><TransactionForm kind="purchase" /></Private>} />
      <Route path="/sales" element={<Private><TransactionForm kind="sale" /></Private>} />
      <Route path="/quotations" element={<Private><Quotations /></Private>} />
      <Route path="/invoice/:id" element={<Private><InvoicePrint /></Private>} />
      <Route path="/quote/:id" element={<Private><InvoicePrint /></Private>} />
      <Route path="/tech-issue" element={<Private><TechIssue /></Private>} />
      <Route path="/tech-return" element={<Private><TechReturn /></Private>} />
      <Route path="/tech-ledger" element={<Private><TechnicianLedger /></Private>} />
      <Route path="/stock-report" element={<Private><StockReport /></Private>} />
      <Route path="/low-stock" element={<Private><LowStock /></Private>} />
      <Route path="/stock-movements" element={<Private><StockMovements /></Private>} />
      <Route path="/settings" element={<Private><Settings /></Private>} />
      <Route path="/profile" element={<Private><Profile /></Private>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster position="top-right" />
    </AuthProvider>
  );
}

export default App;
